import { useEffect, useState } from 'react';
import { X, Upload, CheckCircle2, AlertCircle, FileImage, Loader2, Clock } from 'lucide-react';
import type { PedidoVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { centavosAPesos } from '@shared/centavos';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirComandaWebSiActiva } from '@renderer/lib/impresionNavegador';

interface Props {
  abierto: boolean;
  pedido: PedidoVista | null;
  onClose: () => void;
  onConfirmado: () => void;
}

const EXTENSIONES_PERMITIDAS = ['png', 'jpg', 'jpeg', 'webp', 'pdf'] as const;
type ExtensionPermitida = (typeof EXTENSIONES_PERMITIDAS)[number];
const TAMANO_MAX_MB = 5;

/**
 * Sprint 7.2: paso 4 del flujo delivery. Cobro por transferencia con comprobante obligatorio.
 *
 * Razón de existir: hasta v1.3.0 el delivery se enviaba a cocina sin pago. Si el cliente
 * no transfería, ya estabas comprando los insumos. Compromiso #1 (RN: pedido a cocina = pago verificado).
 *
 * Flujo:
 * 1. Mesero/dueño le dicta al cliente el monto + cuenta (UI lo muestra grande).
 * 2. Cliente transfiere y manda screenshot.
 * 3. Mesero adjunta el archivo aquí.
 * 4. Backend valida magic bytes + tamaño + hash anti-replay.
 * 5. Backend crea Pago(metodo=transferencia) + marca pedido pagado + lo envía a cocina.
 */
export function CobroTransferenciaModal({ abierto, pedido, onClose, onConfirmado }: Props) {
  const [archivo, setArchivo] = useState<File | null>(null);
  /**
   * Sprint 77 — Cuánto dice la captura, en pesos y como texto.
   *
   * El negocio ya revisa ese monto a mano y pidió ayuda para leerlo: *"sí se revisa, ojalá el
   * sistema pudiera ayudarme a LEER"*. Este campo es la mitad barata de esa ayuda —teclear lo que
   * se ve son dos segundos y el sistema compara— y es sobre el que se montará después el lector
   * de imagen, si un prototipo demuestra que lee bien las capturas de los bancos reales.
   *
   * Vacío es válido: obligar a capturarlo con el cliente esperando sería la forma segura de que
   * se teclee cualquier cosa con tal de avanzar.
   */
  const [montoCaptura, setMontoCaptura] = useState('');
  /** Se enciende cuando el operador decide continuar con una diferencia a la vista. */
  const [autorizarDiferencia, setAutorizarDiferencia] = useState(false);
  /**
   * Sprint 79 — Cómo se confirma este pago.
   *
   * Reporte de la dueña: *"no siempre tengo la imagen del pago... otros nada más se refleja en mi
   * cuenta"*, y *"a veces se me juntan muchos pedidos y no da tiempo"* de bajar la captura de
   * WhatsApp. La regla nunca fue "tiene que haber una foto": era que alguien verificara que el
   * dinero entró antes de cocinar. Las dos vías cierran la venta; la segunda queda firmada.
   */
  /**
   * Sprint 90 — Nace en "ya me llegó a la cuenta", no en la captura. El porqué está junto a los dos
   * botones, más abajo: su verdad es la notificación del banco, no la foto.
   */
  const [modo, setModo] = useState<'imagen' | 'verificado'>('verificado');
  const [notaVerificacion, setNotaVerificacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exitoso, setExitoso] = useState(false);
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';

  useEffect(() => {
    if (abierto) {
      setArchivo(null);
      setMontoCaptura('');
      setAutorizarDiferencia(false);
      // Sprint 90 — Cada vez que se abre, vuelve al camino que sí usa.
      setModo('verificado');
      setNotaVerificacion('');
      setError(null);
      setExitoso(false);
      setEnviando(false);
    }
  }, [abierto]);

  if (!abierto || !pedido) return null;

  const totalCobrar = pedido.total;
  const tieneEnvio = pedido.costoEnvio !== null && pedido.costoEnvio !== undefined && pedido.costoEnvio > 0;
  const envioContraEntrega = pedido.envioPagaCliente === true;

  const handleSeleccionArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) {
      setArchivo(null);
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!EXTENSIONES_PERMITIDAS.includes(ext as ExtensionPermitida)) {
      setError(`Formato no soportado. Usa: ${EXTENSIONES_PERMITIDAS.join(', ')}.`);
      setArchivo(null);
      return;
    }
    if (file.size > TAMANO_MAX_MB * 1024 * 1024) {
      setError(`El archivo excede ${TAMANO_MAX_MB} MB.`);
      setArchivo(null);
      return;
    }
    setArchivo(file);
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Error leyendo archivo'));
      reader.readAsDataURL(file);
    });

  const confirmar = async () => {
    if (!sesion) return;

    /**
     * Sprint 79 — Sin imagen: la dueña afirma que vio el depósito y su firma queda en el pedido.
     *
     * La comanda sale igual que con foto, porque la garantía que importa —alguien verificó que
     * el dinero entró antes de gastar la comida— se cumple en los dos casos.
     */
    if (modo === 'verificado') {
      setEnviando(true);
      setError(null);
      const confirmado = await window.api.pedidos.confirmarPagoVerificado({
        pedidoId: pedido.id,
        montoVerificado: montoDeclaradoCentavos ?? undefined,
        nota: notaVerificacion.trim() || undefined,
      });
      if (!confirmado.ok) {
        setError(traducirErrorBackend(confirmado.error));
        setEnviando(false);
        return;
      }
      const enviarSinFoto = await window.api.pedidos.enviarACocina(pedido.id);
      if (!enviarSinFoto.ok) {
        setError(traducirErrorBackend(enviarSinFoto.error));
        setEnviando(false);
        return;
      }
      if (esWeb()) await imprimirComandaWebSiActiva(pedido.id);
      setExitoso(true);
      setEnviando(false);
      return;
    }

    if (!archivo) return;
    setEnviando(true);
    setError(null);
    try {
      const base64 = await fileToBase64(archivo);
      const ext = (archivo.name.split('.').pop()?.toLowerCase() ?? '') as ExtensionPermitida;
      const adjuntar = await window.api.pedidos.adjuntarComprobante({
        pedidoId: pedido.id,
        archivoBase64: base64,
        extension: ext,
        usuarioId: sesion.usuarioId,
        montoDeclarado: montoDeclaradoCentavos ?? undefined,
        autorizarDiferencia: autorizarDiferencia || undefined,
      });
      if (!adjuntar.ok) {
        /**
         * Si el backend rebotó por la diferencia, se ofrece autorizarla en vez de dejar al
         * operador atorado: la decisión es suya y hay razones legítimas (un abono, una comisión
         * bancaria, un cliente de confianza). Lo que no puede pasar es que se cuele sin que
         * quede su nombre.
         */
        setError(traducirErrorBackend(adjuntar.error));
        if (/captura dice/i.test(adjuntar.error ?? '')) setAutorizarDiferencia(false);
        setEnviando(false);
        return;
      }
      const enviar = await window.api.pedidos.enviarACocina(pedido.id);
      if (!enviar.ok) {
        setError(traducirErrorBackend(enviar.error));
        setEnviando(false);
        return;
      }

      // Sprint 56 — en desktop la comanda ya salió por la térmica de cocina
      // (fire-and-forget en el handler). En web la dispara el navegador.
      if (esWeb()) await imprimirComandaWebSiActiva(pedido.id);

      setExitoso(true);
      setEnviando(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error inesperado al subir comprobante';
      setError(msg);
      setEnviando(false);
    }
  };

  /**
   * Lo tecleado, en centavos. `null` = no se capturó, que es distinto de capturar 0.
   *
   * Sprint 39 — el dinero viaja en centavos; la interfaz habla en pesos.
   */
  const montoDeclaradoCentavos = (() => {
    const limpio = montoCaptura.replace(/[^\d.,]/g, '').replace(',', '.').trim();
    if (!limpio) return null;
    const n = Number(limpio);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  })();
  const diferencia =
    montoDeclaradoCentavos === null ? 0 : montoDeclaradoCentavos - totalCobrar;
  const cuadra = montoDeclaradoCentavos !== null && diferencia === 0;

  const cerrarYContinuar = () => {
    setExitoso(false);
    onConfirmado();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] flex flex-col">
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-bold">Cobro por transferencia</h2>
          {!enviando && !exitoso && (
            <button onClick={onClose} className="btn-ghost p-1" aria-label="Cerrar">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {!exitoso ? (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 px-5 py-4">
              {/*
                Sprint 98 — Ya no dice "Paso 4 de 4".

                El cobro no es un paso de captura: es una **espera** del cliente y del banco, y
                puede durar horas. Numerarlo como si fuera el último tramo de un asistente le decía
                al operador que tenía que terminarlo ahí mismo, y por eso esta ventana se sentía
                como un cepo. El pedido ya existe y ya está guardado desde antes de abrirla.
              */}
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">
                <p className="font-semibold text-amber-900">Cobrar antes de cocinar</p>
                <p className="mt-1 text-xs text-amber-800">
                  Pídele al cliente que transfiera el total. No mandamos a cocina hasta que el
                  pago esté confirmado — con su captura o porque tú ya lo viste en tu cuenta.
                  <span className="mt-1 block font-medium">
                    Si se va a tardar, no te quedes aquí: déjalo esperando y sigue atendiendo.
                  </span>
                </p>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">Pedido</div>
                <div className="font-mono text-sm font-bold">
                  #{String(pedido.numeroPedido).padStart(5, '0')}
                </div>
                {pedido.clienteNombre && (
                  <div className="text-sm text-slate-700 mt-1">{pedido.clienteNombre}</div>
                )}
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-sm text-slate-600">Total a transferir</span>
                  <span className="text-3xl font-bold text-primary tabular-nums">
                    {monedaSimbolo}{centavosAPesos(totalCobrar).toFixed(2)}
                  </span>
                </div>
                {tieneEnvio && envioContraEntrega && pedido.costoEnvio && (
                  <p className="mt-1 text-xs text-slate-500">
                    + {monedaSimbolo}{centavosAPesos(pedido.costoEnvio).toFixed(2)} de envío que el cliente paga al recibir.
                  </p>
                )}
              </div>

              {/*
                Sprint 79 — Cómo se confirma el pago. Dos caminos, los dos válidos.

                Lo pidió la dueña con el sistema en la mano: *"si lo tengo se lo pongo, pero si no,
                saltar ese paso"*, *"quizá un botón de: sin comprobante o algo así"*. Y también
                pidió no quitar el otro: *"por si el cliente lo manda, así queda evidencia"*.

                Se presenta como dos opciones a la misma altura, no como "lo normal" y "el atajo":
                en su operación las dos son igual de normales.
              */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {/*
                  Sprint 90 — Primero el camino que de verdad usa.

                  El 79 los puso a la misma altura, y era correcto entonces. Dejó de serlo: *"ella
                  verifica **aunque le pasen captura** — ahora ya se pueden generar con IA, ya no se
                  confía"*. Su verdad es la notificación del banco; la foto es evidencia que se
                  guarda, no prueba de pago.
                */}
                <button
                  type="button"
                  onClick={() => {
                    setModo('verificado');
                    setError(null);
                  }}
                  className={`min-h-[52px] rounded-lg border-2 px-3 py-2 text-left text-sm ${
                    modo === 'verificado'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  disabled={enviando}
                >
                  <span className="block font-semibold">Ya me llegó a la cuenta</span>
                  <span className="block text-xs opacity-80">
                    Lo viste en tu banco · queda con tu nombre
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModo('imagen');
                    setError(null);
                  }}
                  className={`min-h-[52px] rounded-lg border-2 px-3 py-2 text-left text-sm ${
                    modo === 'imagen'
                      ? 'border-primary bg-blue-50 text-blue-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  disabled={enviando}
                >
                  <span className="block font-semibold">Guardar su captura</span>
                  <span className="block text-xs opacity-80">
                    Como evidencia · no sustituye verla en tu banco
                  </span>
                </button>
              </div>

              {modo === 'verificado' && (
                <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-sm text-emerald-900">
                    Vas a confirmar que <b>el depósito ya está en la cuenta del negocio</b>. Queda
                    registrado con tu nombre y la hora, y el pedido entra a cocina.
                  </p>
                  <div>
                    <label className="label" htmlFor="notaVerif">
                      Nota (opcional)
                    </label>
                    <input
                      id="notaVerif"
                      className="input"
                      value={notaVerificacion}
                      onChange={(e) => setNotaVerificacion(e.target.value)}
                      placeholder="Depósito 11:42, me avisó por WhatsApp…"
                      maxLength={200}
                      disabled={enviando}
                    />
                  </div>
                </div>
              )}

              <div className={modo === 'imagen' ? '' : 'hidden'}>
                <label className="label" htmlFor="comprobante">
                  Comprobante de transferencia
                </label>
                <p className="mb-2 text-xs text-slate-500">
                  Imagen o PDF, máximo {TAMANO_MAX_MB} MB. Formatos: png, jpg, jpeg, webp, pdf.
                </p>
                <label
                  htmlFor="comprobante"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-6 hover:border-primary hover:bg-blue-50"
                >
                  {archivo ? (
                    <>
                      <FileImage className="h-8 w-8 text-emerald-600" />
                      <div className="text-sm font-medium">{archivo.name}</div>
                      <div className="text-xs text-slate-500">
                        {(archivo.size / 1024).toFixed(0)} KB · click para cambiar
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-slate-400" />
                      <div className="text-sm font-medium text-slate-700">
                        Click para seleccionar archivo
                      </div>
                      <div className="text-xs text-slate-500">
                        Subir captura o PDF
                      </div>
                    </>
                  )}
                </label>
                <input
                  id="comprobante"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf"
                  onChange={handleSeleccionArchivo}
                  className="hidden"
                  disabled={enviando}
                />
              </div>

              {/*
                Sprint 77 — "¿Cuánto dice la captura?".

                El sistema no adivina: compara lo que el operador lee con lo que el pedido dice.
                Si no cuadra, lo enseña en pesos —"faltan $235"— porque una diferencia contada en
                dinero se entiende y una en porcentajes no.
              */}
              <div>
                <label className="label" htmlFor="montoCaptura">
                  {modo === 'imagen' ? '¿Cuánto dice la captura?' : '¿Cuánto te llegó a la cuenta?'}
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">{monedaSimbolo}</span>
                    <input
                      id="montoCaptura"
                      className="input w-36 text-lg tabular-nums"
                      inputMode="decimal"
                      value={montoCaptura}
                      onChange={(e) => {
                        setMontoCaptura(e.target.value);
                        setAutorizarDiferencia(false);
                      }}
                      placeholder={centavosAPesos(totalCobrar).toFixed(2)}
                      disabled={enviando}
                    />
                  </div>
                  {cuadra && (
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      ✓ Coincide con el total
                    </span>
                  )}
                  {montoDeclaradoCentavos !== null && diferencia !== 0 && (
                    <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">
                      {diferencia < 0 ? 'Faltan' : 'Sobran'} {monedaSimbolo}
                      {(Math.abs(diferencia) / 100).toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Opcional, pero si lo capturas el sistema te avisa cuando no cuadra.
                </p>

                {montoDeclaradoCentavos !== null && diferencia !== 0 && (
                  <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={autorizarDiferencia}
                      onChange={(e) => setAutorizarDiferencia(e.target.checked)}
                      disabled={enviando}
                    />
                    <span className="text-rose-900">
                      Aceptar el pago aunque no cuadre.
                      <span className="mt-0.5 block text-xs text-rose-700">
                        Queda registrado con tu nombre y con la diferencia.
                      </span>
                    </span>
                  </label>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" role="alert">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/*
              Sprint 98 — La salida que faltaba, con nombre y sin miedo.

              Reporte del dueño del producto: *"la ventana de cobro por transferencia bloquea la
              continuidad operacional… ¿qué tal que el cliente tiene que ir a un OXXO a hacer el
              pago?"*. Y tenía razón en lo que importa: la salida existía —el botón decía
              **"Cancelar"**—, pero esa palabra, en la pantalla que decide si el pedido entra a
              cocina, se lee como *"cancelar el pedido"*. Nadie la aprieta con el cliente al
              teléfono, así que en la práctica la ventana era un cepo.

              No se agrega ningún estado nuevo: el pedido ya está creado y ya quedó en
              `pendiente_pago` desde antes de abrir esto. Lo único que cambia es que ahora **se
              dice** a dónde va y que se puede volver por él. Es la misma lección del Sprint 76 con
              la cotización del repartidor: las esperas que dependen de un tercero se dejan
              esperando, no se sostienen con la pantalla abierta.
            */}
            <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <p className="mb-2 text-xs text-slate-600">
                ¿Va al cajero, al OXXO, o dijo que transfiere al rato? Déjalo esperando: el pedido
                #{String(pedido.numeroPedido).padStart(5, '0')} te espera en{' '}
                <span className="font-semibold">A domicilio → Esperando respuesta</span>, marcado
                como <span className="font-semibold">Falta el comprobante</span>. Nada se pierde y
                la cocina no lo recibe hasta que confirmes el pago.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-secondary" disabled={enviando}>
                  <Clock className="h-4 w-4" />
                  Está pagando · dejarlo esperando
                </button>
                {/*
                  Sprint 77 — Con una diferencia sin autorizar, el botón se bloquea aquí además de
                  rebotar en el servidor. El bloqueo del cliente es cortesía —evita el viaje y el
                  mensaje de error—; la autoridad sigue estando en el backend, que revisa lo mismo
                  aunque esta pantalla mienta.
                */}
                <button
                  onClick={confirmar}
                  disabled={
                    enviando ||
                    (modo === 'imagen' && !archivo) ||
                    (diferencia !== 0 && !autorizarDiferencia)
                  }
                  className="btn-primary"
                >
                  {enviando ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Validando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Confirmar pago y enviar a cocina
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="px-5 py-8 text-center">
            <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
            <h3 className="mt-3 text-xl font-bold">¡Pedido enviado a cocina!</h3>
            <p className="mt-1 text-sm text-slate-600">
              Pago confirmado. La cocina ya recibió el pedido #{String(pedido.numeroPedido).padStart(5, '0')}.
            </p>
            <button onClick={cerrarYContinuar} className="btn-primary btn-lg mt-5">
              Continuar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
